// #ux-redesign-start: DataTable primitive (Paso 2)
// Wrapper sobre react-table 7 + Reactstrap Table. Encapsula la lógica de tabla
// con sort + pagination + loading + empty que hoy se duplica en 15+ vistas.
//
// PORTABILIDAD: trasplantable a Web-Patients (mismo react-table 7 está disponible).
//
// USO:
//   const columns = useMemo(() => [
//     { Header: 'Datum', accessor: 'date', Cell: ({ value }) => format(value) },
//     { Header: 'Patient', accessor: 'patient' },
//     { Header: 'Status', accessor: 'status', Cell: ({ value }) => <Badge variant="success">{value}</Badge> },
//   ], []);
//
//   <DataTable
//     columns={columns}
//     data={rows}
//     loading={loading}
//     empty={<EmptyState title="Keine Daten" />}
//     density="comfortable"   // 'compact' | 'regular' | 'comfortable'
//     onRowClick={(row) => history.push(`/admin/labor/${row._id}`)}
//     pagination={{
//       page, pageSize, total, lastPage,
//       onPageChange: setPage,
//       onPageSizeChange: setPageSize,
//     }}
//     selection={{
//       selectedIds: ['abc', 'def'],
//       onToggle: (id) => dispatch(toggle(id)),
//       onToggleAll: (visibleIds) => dispatch(toggleAll(visibleIds)),
//       getRowId: (row) => String(row._id),  // default: row._id
//     }}
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';
import { useTable, useSortBy, usePagination } from 'react-table';
import { useTranslate } from 'react-translate';
// #ux-redesign: loading interno del DataTable también usa el spinner unificado
import Spinner from './Spinner';

const DataTable = ({
  columns,
  data,
  loading = false,
  empty = null,
  density = 'regular',
  manualPagination = true,
  manualSortBy = true,
  onRowClick,
  onSortChange,
  pagination,
  rowClassName,
  selection,
  className = '',
}) => {
  const ta = useTranslate('a11y');
  // #ux-redesign: si el consumer pide selection, inyectamos una columna
  // checkbox al inicio. La columna es no-sorteable y bloquea el row click
  // del propio checkbox para que no dispare onRowClick.
  const augmentedColumns = React.useMemo(() => {
    if (!selection) return columns;
    const getRowId = selection.getRowId || ((row) => String(row?._id ?? ''));
    const visibleIds = (data || []).map(getRowId).filter(Boolean);
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => (selection.selectedIds || []).includes(id));
    const someSelected =
      !allSelected && visibleIds.some((id) => (selection.selectedIds || []).includes(id));

    const selectColumn = {
      id: '__select',
      disableSortBy: true,
      Header: () => (
        <input
          type="checkbox"
          aria-label={ta('select-all')}
          className="ds-table__checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onClick={(e) => e.stopPropagation()}
          onChange={() => selection.onToggleAll && selection.onToggleAll(visibleIds)}
        />
      ),
      Cell: ({ row }) => {
        const id = getRowId(row.original);
        const checked = (selection.selectedIds || []).includes(id);
        return (
          <input
            type="checkbox"
            aria-label={ta('select-row')}
            className="ds-table__checkbox"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => selection.onToggle && selection.onToggle(id)}
          />
        );
      },
    };
    return [selectColumn, ...columns];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, data, selection]);

  const tableInstance = useTable(
    {
      columns: augmentedColumns,
      data: data || [],
      manualPagination,
      manualSortBy,
      pageCount: pagination?.lastPage ? pagination.lastPage + 1 : -1,
      // Paginado y sort son server-side (manual). Sin esto, react-table
      // resetea su estado interno cada vez que cambia `data` (nuevo array en
      // cada fetch) y dispara onSortChange/onPageChange espurios → si el
      // consumidor re-fetchea en esos callbacks, se genera un loop infinito.
      autoResetSortBy: false,
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows: tableRows,
    prepareRow,
    state: { sortBy },
  } = tableInstance;

  // Notificar cambios de sort al consumidor — sólo ante cambios reales del
  // usuario, no en el mount inicial (evita un fetch redundante y cualquier
  // realimentación con el estado de datos del consumidor).
  const sortByJson = JSON.stringify(sortBy);
  const didMountSort = React.useRef(false);
  React.useEffect(() => {
    if (!didMountSort.current) {
      didMountSort.current = true;
      return;
    }
    if (onSortChange) onSortChange(sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByJson]);

  const tableClasses = [
    'ds-table',
    `ds-table--density-${density}`,
    onRowClick ? 'ds-table--interactive' : '',
    className,
  ].filter(Boolean).join(' ');

  const renderEmpty = () => {
    if (loading) return null;
    if (tableRows.length > 0) return null;
    return (
      <div className="ds-table__empty">
        {empty}
      </div>
    );
  };

  return (
    <div className="ds-table-wrapper">
      <div className="ds-table-scroll">
        <table {...getTableProps()} className={tableClasses}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...rest}>
                  {headerGroup.headers.map((column) => {
                    const sortToggleProps = column.canSort
                      ? column.getSortByToggleProps()
                      : {};
                    const { key: ck, ...cprops } = column.getHeaderProps(sortToggleProps);
                    return (
                      <th
                        key={ck}
                        {...cprops}
                        className={`ds-table__th ${column.canSort ? 'ds-table__th--sortable' : ''}`}
                      >
                        <span className="ds-table__th-label">
                          {column.render('Header')}
                          {column.isSorted && (
                            <span className="ds-table__sort-icon" aria-hidden="true">
                              {column.isSortedDesc ? '▼' : '▲'}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading && (
              <tr>
                <td colSpan={augmentedColumns.length} className="ds-table__loading">
                  <Spinner />
                </td>
              </tr>
            )}
            {!loading && tableRows.map((row) => {
              prepareRow(row);
              const { key, ...rest } = row.getRowProps();
              const extraCls = typeof rowClassName === 'function' ? rowClassName(row.original) : (rowClassName || '');
              const rowId = selection
                ? (selection.getRowId || ((r) => String(r?._id ?? '')))(row.original)
                : null;
              const isSelected = selection && (selection.selectedIds || []).includes(rowId);
              return (
                <tr
                  key={key}
                  {...rest}
                  className={`ds-table__row ${isSelected ? 'ds-table__row--selected' : ''} ${extraCls}`}
                  onClick={onRowClick ? () => onRowClick(row.original, row) : undefined}
                >
                  {row.cells.map((cell) => {
                    const { key: ck, ...cprops } = cell.getCellProps();
                    return (
                      <td key={ck} {...cprops} className="ds-table__td">
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {renderEmpty()}
      {pagination && tableRows.length > 0 && (
        <Pagination {...pagination} />
      )}
    </div>
  );
};

// #ux-redesign: paginador moderno sin números de página.
// Tres modos según lo que sepa el consumidor:
//   1. `total > 0` → "X–Y von Z" (range + total).
//   2. `pageItemCount` provisto pero sin `total` → "X–Y" (range, sin total).
//   3. Nada de info → sólo prev/next + pageSize.
// La idea: el usuario piensa en items, no en páginas. Si no tenemos el total
// (caso APIs sin count, paginación por cursor), no inventamos un número.
const Pagination = ({
  page = 0,
  lastPage = 0,
  pageSize = 15,
  total,
  pageItemCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 15, 25, 50],
}) => {
  const ta = useTranslate('a11y');
  const hasTotal = typeof total === 'number' && total > 0;
  const isEmpty = total === 0 || (pageItemCount === 0 && !hasTotal);

  let info = '';
  if (hasTotal) {
    const from = page * pageSize + 1;
    const to = Math.min((page + 1) * pageSize, total);
    info = `${from}–${to} von ${total}`;
  } else if (typeof pageItemCount === 'number' && pageItemCount > 0) {
    const from = page * pageSize + 1;
    const to = page * pageSize + pageItemCount;
    info = `${from}–${to}`;
  } else if (isEmpty) {
    info = '0 Einträge';
  }

  const canPrev = page > 0;
  const canNext = page < lastPage;

  return (
    <div className="ds-table__pagination">
      <div className="ds-table__pagination-info">{info}</div>
      <div className="ds-table__pagination-controls">
        {onPageSizeChange && (
          <select
            className="ds-table__pagesize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            aria-label={ta('rows-per-page')}
          >
            {pageSizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button
          type="button"
          className="ds-table__pagebtn"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label={ta('prev-page')}
        >‹</button>
        <button
          type="button"
          className="ds-table__pagebtn"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          aria-label={ta('next-page')}
        >›</button>
      </div>
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  empty: PropTypes.node,
  density: PropTypes.oneOf(['compact', 'regular', 'comfortable']),
  manualPagination: PropTypes.bool,
  manualSortBy: PropTypes.bool,
  onRowClick: PropTypes.func,
  onSortChange: PropTypes.func,
  pagination: PropTypes.object,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  // #ux-redesign: selection support (checkbox column + select-all).
  selection: PropTypes.shape({
    selectedIds: PropTypes.array,
    onToggle: PropTypes.func,
    onToggleAll: PropTypes.func,
    getRowId: PropTypes.func,
  }),
  className: PropTypes.string,
};

export default DataTable;
// #ux-redesign-end
